import React, { useMemo } from 'react'
import { Stack, Typography, useTheme, Tooltip } from '@mui/material'
import { styled } from '@mui/system'

const StyledWidgetLineInformation = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'hasLink',
})<{ hasLink?: boolean }>(({ theme, hasLink }) => ({
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: '24px',
  cursor: hasLink ? 'pointer' : 'default',
  width: '100%',
  '&:hover': {
    boxShadow: hasLink ? '0px 4px 12px rgba(0, 0, 0, 0.08)' : 'none',
    borderColor: hasLink ? theme.palette.grey[300] : theme.palette.grey[200],
  },
}))

const LineContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `10px ${theme.spacing(3)}`,
  '&:not(:last-child)': {
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    paddingBottom: theme.spacing(0.7),
  },
}))

const LabelContainer = styled(Stack)(() => ({
  alignItems: 'flex-start',
}))

const InfoContainer = styled(Stack)(() => ({
  alignItems: 'center',
}))

const WidgetLineInformation = ({ lines, onClick = () => { }, hasLink = true }) => {
  const theme = useTheme()

  return (
    <StyledWidgetLineInformation onClick={onClick} hasLink={hasLink}>
      {lines.map((line, index) => {
        const formattedValue = useMemo(() => {
          return typeof line.value === 'number'
            ? new Intl.NumberFormat('fr-FR', { useGrouping: true }).format(line.value)
            : line.value
        }, [line.value])

        const formattedSubValue = useMemo(() => {
          return typeof line.subValue === 'number'
            ? new Intl.NumberFormat('fr-FR', { useGrouping: true }).format(line.subValue)
            : line.subValue
        }, [line.subValue])

        return (
          <LineContainer key={index}>
            <LabelContainer>
              <Typography fontSize={14}>{line.mainLabel}</Typography>
              <Typography fontSize={10} color={theme.palette.grey[600]}>
                {line.labelInfo}
              </Typography>
            </LabelContainer>
            {line.tooltipContent ? (
              <Tooltip title={line.tooltipContent}>
                <InfoContainer>
                  {line.subValue ? (
                    <Stack spacing={0.5} alignItems="flex-end" direction="row">
                      <Typography fontSize={20} lineHeight={1}>
                        {formattedValue}
                      </Typography>
                      <Typography fontSize={12} color={theme.palette.grey[600]}>
                        {formattedSubValue}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography fontSize={20}>{formattedValue}</Typography>
                  )}
                </InfoContainer>
              </Tooltip>
            ) : (
              <InfoContainer>
                {line.subValue ? (
                  <Stack spacing={0.5} alignItems="flex-end" direction="row">
                    <Typography fontSize={20} lineHeight={1}>
                      {formattedValue}
                    </Typography>
                    <Typography fontSize={12} color={theme.palette.grey[600]}>
                      {formattedSubValue}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography fontSize={20}>{formattedValue}</Typography>
                )}
              </InfoContainer>
            )}
          </LineContainer>
        )
      })}
    </StyledWidgetLineInformation>
  )
}

export default WidgetLineInformation
